import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Button, Divider, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Alert, LinearProgress, Breadcrumbs, Link,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Avatar, InputAdornment,
  Paper
} from '@mui/material';
import {
  Edit, ArrowBack, Add, Upload, Download, Delete, CheckCircle,
  Warning, Error as ErrorIcon, Assignment, Description, History, AutoAwesome,
  Search as SearchIcon, Close as CloseIcon, OpenInNew
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { format, parseISO } from 'date-fns';
import { contractsApi, obligationsApi, documentsApi, usersApi, searchApi } from '../../services/api';
import type { Contract, Obligation, ContractDocument } from '../../types';
import AmendmentDialog from './AmendmentDialog';
import ContractGantt from './ContractGantt';
import ClaudeExtractDialog from './ClaudeExtractDialog';
import { useFieldVisibility } from '../../hooks/useFieldVisibility';
import { useUser } from '../../context/UserContext';

function TabPanel({ value, index, children }: any) {
  return value === index ? <Box pt={2}>{children}</Box> : null;
}

function StatusChip({ status }: { status: string }) {
  const map: Record<string, any> = {
    active: { color: 'success' }, expired: { color: 'error' },
    draft: { color: 'default' }, terminated: { color: 'error' }, suspended: { color: 'warning' }
  };
  return <Chip label={status} color={map[status]?.color || 'default'} size="small" sx={{ textTransform: 'capitalize' }} />;
}

function ObligationStatusChip({ status }: { status: string }) {
  const map: Record<string, any> = {
    completed: { color: 'success' }, overdue: { color: 'error' }, at_risk: { color: 'warning' },
    in_progress: { color: 'info' }, pending: { color: 'default' }, waived: { color: 'default' }
  };
  return <Chip label={status.replace('_', ' ')} color={map[status]?.color || 'default'} size="small" sx={{ textTransform: 'capitalize' }} />;
}

function InfoRow({ label, value }: { label: string; value?: any }) {
  return (
    <Box display="flex" py={0.75}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={value ? 400 : undefined} color={value ? 'text.primary' : 'text.disabled'}>
        {value || '-'}
      </Typography>
    </Box>
  );
}

export default function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [contract, setContract] = useState<Contract | null>(null);
  const [obligations, setObligations] = useState<Obligation[]>([]);
  const [documents, setDocuments] = useState<ContractDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  // Download / open failures are shown in the Documents tab, not as a
  // page-level error that would blank out the contract.
  const [docActionError, setDocActionError] = useState<string | null>(null);
  const [docActionBusy, setDocActionBusy] = useState<string | null>(null);
  const [docDuplicate, setDocDuplicate] = useState<{
    file: File;
    canOverride: boolean;
    duplicate: any;
    message: string;
  } | null>(null);
  const [addObligationOpen, setAddObligationOpen] = useState(false);
  const [amendmentOpen, setAmendmentOpen] = useState(false);
  const [amendments, setAmendments] = useState<any[]>([]);
  const [claudeExtractOpen, setClaudeExtractOpen] = useState(false);
  const [userRole, setUserRole] = useState<string>('viewer');
  // Use effectiveRole so admin role-impersonation also gates these write actions
  const { effectiveRole } = useUser();
  const canEdit = effectiveRole !== 'viewer';
  const canDelete = effectiveRole === 'admin' || effectiveRole === 'manager';
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [childContracts, setChildContracts] = useState<any[]>([]);
  const { isVisible } = useFieldVisibility(contract?.contract_type || '');

  // Document full-text search state
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [docSearchResults, setDocSearchResults] = useState<any[]>([]);
  const [docSearchLoading, setDocSearchLoading] = useState(false);
  const [docSearchRan, setDocSearchRan] = useState(false);
  const docSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [contractRes, amendRes, meRes] = await Promise.all([
        contractsApi.get(id),
        contractsApi.getAmendments(id),
        usersApi.me()
      ]);
      setContract(contractRes.data);
      setObligations(contractRes.data.obligations || []);
      setDocuments(contractRes.data.documents || []);
      setAmendments(amendRes.data || []);
      setChildContracts(contractRes.data.child_contracts || []);
      setUserRole(meRes.data?.role || 'viewer');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load contract');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff']
    },
    onDrop: async (files) => {
      if (!id || files.length === 0) return;
      await uploadFiles(files);
    }
  });

  async function uploadFiles(files: File[], force = false) {
    if (!id) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_type', 'original');
      formData.append('is_primary', documents.length === 0 ? 'true' : 'false');
      try {
        await documentsApi.upload(id, formData, setUploadProgress, force);
      } catch (err: any) {
        const status = err.response?.status;
        const data = err.response?.data;
        if ((status === 403 || status === 409) && data?.error === 'duplicate_document') {
          // Stop the loop on duplicate so we can show the dialog
          setDocDuplicate({
            file,
            canOverride: !!data.can_override,
            duplicate: data.duplicate,
            message: data.message
          });
          break;
        }
        setError(data?.error || 'Upload failed');
      }
    }
    setUploading(false);
    setUploadProgress(0);
    load();
  }

  // Axios error bodies come back as a Blob when responseType is 'blob' —
  // read it so the user sees the real reason instead of a silent no-op.
  const readBlobError = async (err: any, fallback: string) => {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        if (parsed?.error) return parsed.error;
      } catch {}
    }
    return data?.error || err?.message || fallback;
  };

  const handleDownload = async (doc: ContractDocument) => {
    setDocActionError(null);
    setDocActionBusy(doc.id);
    try {
      const res = await documentsApi.download(doc.id);
      const blob = new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.document_name;
      // Safari/Firefox ignore clicks on detached anchors, and revoking the URL
      // synchronously cancels the download before the browser has read it.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      setDocActionError(await readBlobError(err, 'Download failed'));
    }
    setDocActionBusy(null);
  };

  // Open the document in a new tab. Browsers can render PDFs and images
  // inline; anything else (Word, etc.) is downloaded instead.
  const handleOpen = async (doc: ContractDocument) => {
    const mime = doc.mime_type || '';
    const viewable = mime === 'application/pdf' || mime.startsWith('image/');
    if (!viewable) return handleDownload(doc);

    // Open the tab inside the click gesture so the popup blocker allows it.
    const tab = window.open('', '_blank');
    setDocActionError(null);
    setDocActionBusy(doc.id);
    try {
      const res = await documentsApi.download(doc.id, true);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      tab?.close();
      setDocActionError(await readBlobError(err, 'Could not open document'));
    }
    setDocActionBusy(null);
  };

  const handleDocSearch = useCallback((q: string) => {
    if (docSearchDebounce.current) clearTimeout(docSearchDebounce.current);
    if (!q.trim() || q.trim().length < 2) {
      setDocSearchResults([]);
      setDocSearchRan(false);
      return;
    }
    docSearchDebounce.current = setTimeout(async () => {
      if (!id) return;
      setDocSearchLoading(true);
      setDocSearchRan(true);
      try {
        const res = await searchApi.contract(id, q.trim());
        setDocSearchResults(res.data.snippets || []);
      } catch {
        setDocSearchResults([]);
      } finally {
        setDocSearchLoading(false);
      }
    }, 350);
  }, [id]);

  const formatDate = (d?: string) => d ? format(parseISO(d), 'MMM d, yyyy') : undefined;
  const formatCurrency = (v?: number, currency = 'USD') =>
    v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(v) : undefined;

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!contract) return null;

  const daysLeft = contract.days_until_expiration;
  const isExpiringSoon = daysLeft !== undefined && daysLeft !== null && daysLeft <= 90 && daysLeft >= 0;
  const isExpired = daysLeft !== undefined && daysLeft !== null && daysLeft < 0;

  return (
    <Box>
      {/* Header */}
      <Box mb={docSearchRan && docSearchResults.length > 0 ? 1 : 3}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/contracts')}>Contracts</Link>
          <Typography color="text.primary" noWrap sx={{ maxWidth: 300 }}>{contract.contract_name}</Typography>
        </Breadcrumbs>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="h5" fontWeight={600}>{contract.contract_name}</Typography>
              <StatusChip status={contract.status} />
              <Chip label={contract.contract_type} size="small" variant="outlined" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {contract.counterparty_name} · {contract.contract_number ? `#${contract.contract_number}` : 'No contract number'}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            {canEdit && (
              <>
                <Button startIcon={<AutoAwesome />} variant="outlined" color="secondary" onClick={() => setClaudeExtractOpen(true)}>
                  Extract with Claude
                </Button>
                <Button variant="outlined" onClick={() => setAmendmentOpen(true)}>
                  Add Amendment
                </Button>
                <Button startIcon={<Edit />} variant="contained" onClick={() => navigate(`/contracts/${id}/edit`)}>
                  Edit
                </Button>
              </>
            )}
            {canDelete && (
              <Button startIcon={<Delete />} variant="outlined" color="error" onClick={() => setDeleteConfirmOpen(true)}>
                Delete
              </Button>
            )}
          </Box>
        </Box>

        {/* Document Full-Text Search — header row */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search this contract's document text…"
          value={docSearchQuery}
          onChange={(e) => {
            setDocSearchQuery(e.target.value);
            handleDocSearch(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {docSearchLoading
                  ? <CircularProgress size={16} />
                  : <SearchIcon fontSize="small" color="action" />
                }
              </InputAdornment>
            ),
            endAdornment: docSearchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => {
                  setDocSearchQuery('');
                  setDocSearchResults([]);
                  setDocSearchRan(false);
                }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }}
          sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
        />

        {/* Inline search results — expands below the search bar */}
        {docSearchRan && !docSearchLoading && docSearchResults.length === 0 && (
          <Paper variant="outlined" sx={{ mt: 0.5, px: 2, py: 1.5, borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              No matches found for "{docSearchQuery}" in this contract's documents.
            </Typography>
          </Paper>
        )}
        {docSearchResults.length > 0 && (
          <Paper variant="outlined" sx={{ mt: 0.5, p: 2, borderRadius: 1, maxHeight: 340, overflowY: 'auto' }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {docSearchResults.length} passage{docSearchResults.length !== 1 ? 's' : ''} matching "{docSearchQuery}"
            </Typography>
            {docSearchResults.map((result: any, i: number) => (
              <Box
                key={i}
                sx={{
                  mb: i < docSearchResults.length - 1 ? 1.5 : 0,
                  pb: i < docSearchResults.length - 1 ? 1.5 : 0,
                  borderBottom: i < docSearchResults.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider'
                }}
              >
                <Typography variant="caption" color="primary.main" fontWeight={600} display="block" mb={0.5}>
                  <Description sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                  {result.document_name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ lineHeight: 1.7, fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  <HighlightedText text={result.snippet} query={docSearchQuery} />
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
      </Box>

      {/* Expiration Alert */}
      {isExpired && (
        <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
          This contract expired {Math.abs(daysLeft!)} days ago on {formatDate(contract.expiration_date)}.
        </Alert>
      )}
      {isExpiringSoon && !isExpired && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          This contract expires in {daysLeft} days on {formatDate(contract.expiration_date)}.
          {contract.renewal_notice_date && ` Renewal notice deadline: ${formatDate(contract.renewal_notice_date)}.`}
        </Alert>
      )}
      {(contract.overdue_obligations || 0) > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {contract.overdue_obligations} overdue obligation{contract.overdue_obligations !== 1 ? 's' : ''} require attention.
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} mb={2}>
        {[
          { label: 'Effective Date', value: formatDate(contract.effective_date) },
          { label: 'Expiration Date', value: formatDate(contract.expiration_date) },
          { label: 'Notice Deadline', value: formatDate(contract.renewal_notice_date) },
          { label: 'Contract Value', value: formatCurrency(contract.contract_value, contract.currency) },
          contract.mrc != null ? { label: 'MRC', value: formatCurrency(contract.mrc, contract.currency) } : null,
          contract.nrc != null ? { label: 'NRC', value: formatCurrency(contract.nrc, contract.currency) } : null,
          contract.liability_cap != null ? { label: 'Liability Cap', value: formatCurrency(contract.liability_cap, contract.currency) }
            : contract.liability_cap_multiplier ? { label: 'Liability Cap', value: contract.liability_cap_multiplier } : null,
          { label: 'Notice Period', value: contract.notice_period_days ? `${contract.notice_period_days} days` : undefined },
          { label: 'Auto-Renews', value: contract.auto_renews ? 'Yes' : 'No' },
          contract.sla_uptime_pct != null ? { label: 'Uptime SLA', value: `${contract.sla_uptime_pct}%` } : null,
        ].filter(Boolean).map((item: any) => (
          <Grid item xs={6} sm={4} md={2} key={item.label}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
                <Typography variant="body2" fontWeight={600}>{item.value || '-'}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Details" />
          <Tab label={`Obligations (${obligations.length})`} />
          <Tab label={`Documents (${documents.length})`} />
          <Tab label="Timeline" />
          <Tab label={`Amendments (${amendments.length})`} />
          <Tab label="History" />
          {(childContracts.length > 0 || contract.contract_type === 'MSA') && (
            <Tab label={`Linked (${childContracts.length})`} />
          )}
        </Tabs>

        <CardContent>
          {/* Details Tab */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={3}>

              {/* Contract Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Contract Information</Typography>
                <Divider sx={{ mb: 1 }} />
                {isVisible('contract_number') && <InfoRow label="Contract Number" value={contract.contract_number} />}
                <InfoRow label="Contract Type" value={contract.contract_type} />
                <InfoRow label="Status" value={<StatusChip status={contract.status} />} />
                {isVisible('department') && <InfoRow label="Department" value={contract.department} />}
                {isVisible('legal_entity') && <InfoRow label="DBA / Trade Name" value={contract.legal_entity} />}
                {isVisible('sow_number') && contract.sow_number && <InfoRow label="SOW Number" value={contract.sow_number} />}
                {contract.parent_contract_id && (
                  <Box display="flex" py={0.75}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>Parent Contract</Typography>
                    <Typography
                      variant="body2" color="primary.main"
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => navigate(`/contracts/${contract.parent_contract_id}`)}
                    >
                      {contract.parent_contract_name || contract.parent_contract_id}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Counterparty */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Counterparty</Typography>
                <Divider sx={{ mb: 1 }} />
                <InfoRow label="Name" value={contract.counterparty_name} />
                {isVisible('counterparty_type') && <InfoRow label="Type" value={contract.counterparty_type} />}
                {isVisible('counterparty_company') && <InfoRow label="Company" value={contract.counterparty_company} />}
                {isVisible('counterparty_contact_name') && <InfoRow label="Contact Name" value={contract.counterparty_contact_name} />}
                {isVisible('counterparty_contact_title') && <InfoRow label="Contact Title" value={contract.counterparty_contact_title} />}
                {isVisible('counterparty_contact_email') && <InfoRow label="Contact Email" value={contract.counterparty_contact_email} />}
                {isVisible('address') && <InfoRow label="Address" value={contract.address} />}
              </Grid>

              {/* Dates & Terms */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Dates &amp; Terms</Typography>
                <Divider sx={{ mb: 1 }} />
                {isVisible('execution_date') && <InfoRow label="Execution Date" value={formatDate(contract.execution_date)} />}
                {isVisible('effective_date') && <InfoRow label="Effective Date" value={formatDate(contract.effective_date)} />}
                {isVisible('start_date') && <InfoRow label="Start Date" value={formatDate(contract.start_date)} />}
                {isVisible('expiration_date') && <InfoRow label="Expiration Date" value={formatDate(contract.expiration_date)} />}
                {isVisible('end_date') && <InfoRow label="End Date" value={formatDate(contract.end_date)} />}
                {isVisible('term_length_months') && <InfoRow label="Term Length" value={contract.term_length_months ? `${contract.term_length_months} months` : undefined} />}
                {isVisible('notice_period_days') && <InfoRow label="Notice Period" value={contract.notice_period_days ? `${contract.notice_period_days} days` : undefined} />}
                {isVisible('notice_period_days') && <InfoRow label="Renewal Notice Date" value={formatDate(contract.renewal_notice_date)} />}
                {isVisible('auto_renews') && <InfoRow label="Auto-Renews" value={contract.auto_renews ? 'Yes' : 'No'} />}
                {isVisible('renewal_term_months') && contract.auto_renews ? <InfoRow label="Renewal Term" value={contract.renewal_term_months ? `${contract.renewal_term_months} months` : undefined} /> : null}
                {isVisible('renewal_terms') && contract.renewal_terms && (
                  <Box py={0.75}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, mb: 0.25 }}>Renewal Terms</Typography>
                    <Typography variant="body2">{contract.renewal_terms}</Typography>
                  </Box>
                )}
                {isVisible('renewal_notes') && contract.renewal_notes && (
                  <Box py={0.75}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, mb: 0.25 }}>Renewal Notes</Typography>
                    <Typography variant="body2">{contract.renewal_notes}</Typography>
                  </Box>
                )}
              </Grid>

              {/* Financial Terms */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Financial Terms</Typography>
                <Divider sx={{ mb: 1 }} />
                {isVisible('contract_value') && <InfoRow label="Contract Value" value={formatCurrency(contract.contract_value, contract.currency)} />}
                {isVisible('mrc') && contract.mrc != null && <InfoRow label="MRC" value={formatCurrency(contract.mrc, contract.currency)} />}
                {isVisible('nrc') && contract.nrc != null && <InfoRow label="NRC" value={formatCurrency(contract.nrc, contract.currency)} />}
                {isVisible('currency') && <InfoRow label="Currency" value={contract.currency} />}
                {isVisible('payment_terms') && <InfoRow label="Payment Terms" value={contract.payment_terms} />}
                {isVisible('payment_terms_days') && <InfoRow label="Payment Terms (days)" value={contract.payment_terms_days ? `${contract.payment_terms_days} days` : undefined} />}
                {isVisible('billing_frequency') && <InfoRow label="Billing Frequency" value={contract.billing_frequency} />}
                {isVisible('late_fees_pct') && <InfoRow label="Late Fees" value={contract.late_fees_pct != null ? `${contract.late_fees_pct}%` : undefined} />}
                {isVisible('taxes_pass_through') && <InfoRow label="Taxes / Pass-Through" value={contract.taxes_pass_through} />}
                {isVisible('billing_milestones') && contract.billing_milestones && (
                  <Box py={0.75}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>Billing Milestones</Typography>
                    <Typography variant="body2">{contract.billing_milestones}</Typography>
                  </Box>
                )}
                {isVisible('credits_penalties_refunds') && contract.credits_penalties_refunds && (
                  <Box py={0.75}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>Credits, Penalties &amp; Refunds</Typography>
                    <Typography variant="body2">{contract.credits_penalties_refunds}</Typography>
                  </Box>
                )}
              </Grid>

              {/* Legal & Governance */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Legal &amp; Governance</Typography>
                <Divider sx={{ mb: 1 }} />
                {isVisible('governing_law') && <InfoRow label="Governing Law" value={contract.governing_law} />}
                {isVisible('jurisdiction') && <InfoRow label="Jurisdiction" value={contract.jurisdiction} />}
                {isVisible('venue_jurisdiction') && <InfoRow label="Venue / Jurisdiction" value={contract.venue_jurisdiction} />}
                {isVisible('assignment_rights') && contract.assignment_rights && (
                  <Box py={0.75}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>Assignment Rights</Typography>
                    <Typography variant="body2">{contract.assignment_rights}</Typography>
                  </Box>
                )}
                {isVisible('amendment_requirements') && contract.amendment_requirements && (
                  <Box py={0.75}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>Amendment Requirements</Typography>
                    <Typography variant="body2">{contract.amendment_requirements}</Typography>
                  </Box>
                )}
              </Grid>

              {/* Liability & Risk */}
              {(contract.liability_cap != null || contract.liability_cap_multiplier || contract.indemnification_scope || contract.insurance_requirements || contract.dispute_resolution_method || contract.data_classification) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Liability &amp; Risk</Typography>
                  <Divider sx={{ mb: 1 }} />
                  {contract.liability_cap != null && <InfoRow label="Liability Cap" value={formatCurrency(contract.liability_cap, contract.currency)} />}
                  {contract.liability_cap_multiplier && <InfoRow label="Liability Cap (multiplier)" value={contract.liability_cap_multiplier} />}
                  {contract.dispute_resolution_method && <InfoRow label="Dispute Resolution" value={contract.dispute_resolution_method} />}
                  <InfoRow label="Force Majeure" value={contract.force_majeure ? 'Yes' : 'No'} />
                  {contract.parent_guarantee ? <InfoRow label="Parent Guarantee" value="Required" /> : null}
                  {contract.data_classification && <InfoRow label="Data Classification" value={contract.data_classification} />}
                  {contract.indemnification_scope && (
                    <Box py={0.75}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>Indemnification</Typography>
                      <Typography variant="body2">{contract.indemnification_scope}</Typography>
                    </Box>
                  )}
                  {contract.insurance_requirements && (
                    <Box py={0.75}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>Insurance Requirements</Typography>
                      <Typography variant="body2">{contract.insurance_requirements}</Typography>
                    </Box>
                  )}
                </Grid>
              )}

              {/* Confidentiality */}
              {(contract.confidentiality_period_months != null || contract.non_compete_months != null || contract.non_solicit_months != null || contract.termination_for_convenience_days != null) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Confidentiality &amp; Termination</Typography>
                  <Divider sx={{ mb: 1 }} />
                  {contract.confidentiality_period_months != null && <InfoRow label="Confidentiality Survives" value={`${contract.confidentiality_period_months} months after termination`} />}
                  {contract.non_compete_months != null && <InfoRow label="Non-Compete" value={`${contract.non_compete_months} months`} />}
                  {contract.non_solicit_months != null && <InfoRow label="Non-Solicitation" value={`${contract.non_solicit_months} months`} />}
                  {contract.termination_for_convenience_days != null && <InfoRow label="Termination for Convenience" value={`${contract.termination_for_convenience_days} days notice`} />}
                  {contract.termination_for_cause_days != null && <InfoRow label="Termination for Cause (cure)" value={`${contract.termination_for_cause_days} days`} />}
                </Grid>
              )}

              {/* Fiber & Telecom (IRU) */}
              {(contract.fiber_route || contract.fiber_strand_count != null || contract.fiber_type || contract.bandwidth_gbps != null || contract.mrc != null || contract.nrc != null) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Fiber &amp; Telecom</Typography>
                  <Divider sx={{ mb: 1 }} />
                  {contract.fiber_route && <InfoRow label="Fiber Route" value={contract.fiber_route} />}
                  {contract.fiber_strand_count != null && <InfoRow label="Strand Count" value={`${contract.fiber_strand_count} strands`} />}
                  {contract.fiber_type && <InfoRow label="Fiber Type" value={contract.fiber_type} />}
                  {contract.bandwidth_gbps != null && <InfoRow label="Bandwidth" value={`${contract.bandwidth_gbps} Gbps`} />}
                  {contract.mrc != null && <InfoRow label="MRC" value={formatCurrency(contract.mrc, contract.currency)} />}
                  {contract.nrc != null && <InfoRow label="NRC" value={formatCurrency(contract.nrc, contract.currency)} />}
                </Grid>
              )}

              {/* SLA Metrics */}
              {(contract.sla_uptime_pct != null || contract.sla_response_time_hours != null || contract.sla_credit_pct != null) && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>SLA Metrics</Typography>
                  <Divider sx={{ mb: 1 }} />
                  {contract.sla_uptime_pct != null && <InfoRow label="Committed Uptime" value={`${contract.sla_uptime_pct}%`} />}
                  {contract.sla_response_time_hours != null && <InfoRow label="Response Time SLA" value={`${contract.sla_response_time_hours} hours`} />}
                  {contract.sla_credit_pct != null && <InfoRow label="SLA Credit" value={`${contract.sla_credit_pct}% of monthly fee`} />}
                </Grid>
              )}

              {/* Contract Owner */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Contract Owner</Typography>
                <Divider sx={{ mb: 1 }} />
                <InfoRow label="Name" value={contract.owner_name} />
                <InfoRow label="Email" value={contract.owner_email} />
                <InfoRow label="Department" value={contract.owner_department} />
                <InfoRow label="Title" value={contract.owner_job_title} />
              </Grid>

              {/* Signatures */}
              {isVisible('counterparty_signatory_name') && <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Counterparty Signatory</Typography>
                <Divider sx={{ mb: 1 }} />
                <InfoRow label="Name" value={contract.counterparty_signatory_name} />
                <InfoRow label="Title" value={contract.counterparty_signatory_title} />
                <InfoRow label="Signature Date" value={formatDate(contract.counterparty_signature_date)} />
              </Grid>}
              {isVisible('hyperlink_signatory_name') && <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>HyperLink Signatory</Typography>
                <Divider sx={{ mb: 1 }} />
                <InfoRow label="Name" value={contract.hyperlink_signatory_name} />
                <InfoRow label="Title" value={contract.hyperlink_signatory_title} />
                <InfoRow label="Signature Date" value={formatDate(contract.hyperlink_signature_date)} />
              </Grid>}

              {/* Description & Scope */}
              {((isVisible('description') && contract.description) || (isVisible('scope_summary') && contract.scope_summary)) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Description &amp; Scope</Typography>
                  <Divider sx={{ mb: 1 }} />
                  {isVisible('description') && contract.description && (
                    <Box mb={1.5}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Description</Typography>
                      <Typography variant="body2">{contract.description}</Typography>
                    </Box>
                  )}
                  {isVisible('scope_summary') && contract.scope_summary && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Scope Summary</Typography>
                      <Typography variant="body2">{contract.scope_summary}</Typography>
                    </Box>
                  )}
                </Grid>
              )}

              {/* Key Terms & Tags */}
              {contract.key_terms && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Extracted Key Terms</Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {JSON.parse(contract.key_terms).map((term: string, i: number) => (
                      <Chip key={i} label={term} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              )}
              {contract.tags && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>Tags</Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {(typeof contract.tags === 'string' ? JSON.parse(contract.tags) : contract.tags).map((tag: string, i: number) => (
                      <Chip key={i} label={tag} size="small" />
                    ))}
                  </Box>
                </Grid>
              )}

            </Grid>
          </TabPanel>

          {/* Obligations Tab */}
          <TabPanel value={tab} index={1}>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle2">{obligations.length} obligations linked to this contract</Typography>
              {canEdit && (
                <Button startIcon={<Add />} variant="contained" size="small" onClick={() => navigate(`/obligations?contract_id=${id}&new=true`)}>
                  Add Obligation
                </Button>
              )}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Obligation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {obligations.map((ob) => (
                    <TableRow key={ob.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/obligations/${ob.id}`)}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{ob.obligation_name}</Typography>
                        {ob.sla_metric && <Typography variant="caption" color="text.secondary">SLA: {ob.sla_metric}</Typography>}
                      </TableCell>
                      <TableCell><Chip label={ob.obligation_type} size="small" variant="outlined" /></TableCell>
                      <TableCell><ObligationStatusChip status={ob.status} /></TableCell>
                      <TableCell>
                        <Typography variant="body2">{ob.due_date ? formatDate(ob.due_date) : '-'}</Typography>
                        {ob.days_until_due !== null && ob.days_until_due !== undefined && ob.days_until_due <= 14 && ob.days_until_due >= 0 && (
                          <Typography variant="caption" color="warning.main">{ob.days_until_due}d remaining</Typography>
                        )}
                      </TableCell>
                      <TableCell><Typography variant="body2">{ob.assigned_name || '-'}</Typography></TableCell>
                      <TableCell><IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/obligations/${ob.id}`); }}><Assignment fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                  {obligations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No obligations linked yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Documents Tab */}
          <TabPanel value={tab} index={2}>
            {/* Upload zone — hidden for viewers */}
            {canEdit && (
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed', borderColor: isDragActive ? 'primary.main' : 'divider',
                  borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
                  bgcolor: isDragActive ? 'primary.50' : 'grey.50', mb: 2,
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                  transition: 'all 0.2s'
                }}
              >
                <input {...getInputProps()} />
                <Upload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {isDragActive ? 'Drop files here...' : 'Drag & drop or click to upload (PDF, Word, Images)'}
                </Typography>
              </Box>
            )}
            {uploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />}

            {docActionError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDocActionError(null)}>
                {docActionError}
              </Alert>
            )}

            {/* Duplicate-file dialog */}
            <Dialog open={!!docDuplicate} onClose={() => setDocDuplicate(null)} maxWidth="sm" fullWidth>
              <DialogTitle>
                {docDuplicate?.canOverride ? 'Possible duplicate file' : 'Duplicate file — upload blocked'}
              </DialogTitle>
              <DialogContent>
                <Alert severity={docDuplicate?.canOverride ? 'warning' : 'error'} sx={{ mb: 2 }}>
                  {docDuplicate?.message}
                </Alert>
                {docDuplicate?.duplicate && (
                  <Box sx={{ fontSize: 14, lineHeight: 1.7 }}>
                    <Typography variant="body2">
                      The same file is already attached as <strong>{docDuplicate.duplicate.document_name}</strong>
                      {docDuplicate.duplicate.contract_name && <> on contract <strong>{docDuplicate.duplicate.contract_name}</strong></>}
                      {docDuplicate.duplicate.uploader_name && <>, uploaded by {docDuplicate.duplicate.uploader_name}</>}
                      {docDuplicate.duplicate.uploaded_at && <> on {String(docDuplicate.duplicate.uploaded_at).slice(0, 10)}</>}.
                    </Typography>
                    {docDuplicate.duplicate.contract_id && (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ mt: 1, textTransform: 'none' }}
                        onClick={() => window.open(`/contracts/${docDuplicate.duplicate.contract_id}`, '_blank')}
                      >
                        View original contract →
                      </Button>
                    )}
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDocDuplicate(null)}>Cancel</Button>
                {docDuplicate?.canOverride && (
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => {
                      const f = docDuplicate.file;
                      setDocDuplicate(null);
                      void uploadFiles([f], true);
                    }}
                  >
                    Upload Anyway
                  </Button>
                )}
              </DialogActions>
            </Dialog>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Document</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>OCR</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Description fontSize="small" color="action" />
                          <Box>
                            <Link
                              component="button"
                              type="button"
                              variant="body2"
                              underline="hover"
                              disabled={docActionBusy === doc.id}
                              onClick={() => handleOpen(doc)}
                              sx={{ textAlign: 'left', cursor: 'pointer' }}
                            >
                              {doc.document_name}
                            </Link>
                            {doc.is_primary === 1 && <Chip label="Primary" size="small" color="primary" sx={{ height: 16, fontSize: 10 }} />}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{doc.document_type}</Typography></TableCell>
                      <TableCell><Typography variant="body2">v{doc.version}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2">{format(parseISO(doc.uploaded_at), 'MMM d, yyyy')}</Typography>
                        <Typography variant="caption" color="text.secondary">{doc.uploader_name}</Typography>
                      </TableCell>
                      <TableCell>
                        {doc.ocr_processed ? (
                          <Chip icon={<CheckCircle />} label={`${Math.round((doc.extraction_confidence || 0) * 100)}%`} color="success" size="small" />
                        ) : (
                          <Chip label="Processing..." size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Tooltip title="Open in new tab">
                            <span>
                              <IconButton size="small" disabled={docActionBusy === doc.id} onClick={() => handleOpen(doc)}>
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Download">
                            <span>
                              <IconButton size="small" disabled={docActionBusy === doc.id} onClick={() => handleDownload(doc)}>
                                <Download fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {docActionBusy === doc.id && <CircularProgress size={14} sx={{ ml: 0.5 }} />}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {documents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No documents uploaded yet</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Timeline Tab */}
          <TabPanel value={tab} index={3}>
            <ContractGantt contract={contract} amendments={amendments} />
          </TabPanel>

          {/* Amendments Tab */}
          <TabPanel value={tab} index={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2">{amendments.length} amendment{amendments.length !== 1 ? 's' : ''} applied</Typography>
              {canEdit && (
                <Button variant="contained" size="small" onClick={() => setAmendmentOpen(true)}>Add Amendment</Button>
              )}
            </Box>
            {amendments.length === 0 ? (
              <Typography color="text.secondary">No amendments have been applied to this contract.</Typography>
            ) : (
              amendments.map((a) => (
                <Card key={a.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>{a.amendment_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Applied by {a.created_by_name} · {format(parseISO(a.created_at), 'MMM d, yyyy')}
                          {a.effective_date ? ` · Effective ${format(parseISO(a.effective_date), 'MMM d, yyyy')}` : ''}
                        </Typography>
                      </Box>
                      <Chip label={`#${a.amendment_number}`} size="small" color="primary" variant="outlined" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{a.description}</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>Changes</Typography>
                    <Grid container spacing={1}>
                      {Object.entries(JSON.parse(a.changed_fields || '{}')).map(([field, change]: [string, any]) => (
                        <Grid item xs={12} sm={6} key={field}>
                          <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
                            <Typography variant="caption" fontWeight={600} display="block">{change.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              <span style={{ textDecoration: 'line-through' }}>{change.old ?? '—'}</span>
                              {' → '}
                              <strong>{change.new ?? '—'}</strong>
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              ))
            )}
          </TabPanel>

          {/* History Tab */}
          <TabPanel value={tab} index={5}>
            <ContractHistory contractId={id!} />
          </TabPanel>

          {/* Linked Contracts Tab (index 6 — only rendered for MSAs or contracts with children) */}
          <TabPanel value={tab} index={6}>
            {/* Parent contract link */}
            {contract.parent_contract_id && (
              <Box mb={3}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Parent Contract</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box
                  display="flex" alignItems="center" gap={1.5} p={1.5}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => navigate(`/contracts/${contract.parent_contract_id}`)}
                >
                  <Chip label="MSA" size="small" color="primary" variant="outlined" />
                  <Typography variant="body2" fontWeight={500}>{contract.parent_contract_name || contract.parent_contract_id}</Typography>
                </Box>
              </Box>
            )}

            {/* Child SOWs */}
            {childContracts.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Linked Statements of Work ({childContracts.length})
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SOW / Contract Name</TableCell>
                        <TableCell>SOW #</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Counterparty</TableCell>
                        <TableCell>Effective</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {childContracts.map((child: any) => (
                        <TableRow
                          key={child.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/contracts/${child.id}`)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{child.contract_name}</Typography>
                          </TableCell>
                          <TableCell>{child.sow_number || '-'}</TableCell>
                          <TableCell><StatusChip status={child.status} /></TableCell>
                          <TableCell>{child.counterparty_name}</TableCell>
                          <TableCell>{formatDate(child.effective_date)}</TableCell>
                          <TableCell>{formatDate(child.expiration_date)}</TableCell>
                          <TableCell>{child.contract_value ? formatCurrency(child.contract_value, child.currency) : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Box textAlign="center" py={4} color="text.secondary">
                <Typography variant="body2">No linked contracts found.</Typography>
                <Typography variant="caption">Import a Statement of Work and set this contract as the Parent MSA to link it here.</Typography>
              </Box>
            )}
          </TabPanel>
        </CardContent>
      </Card>

      <AmendmentDialog
        open={amendmentOpen}
        contract={contract}
        amendmentNumber={amendments.length + 1}
        onClose={() => setAmendmentOpen(false)}
        onCreated={() => { setAmendmentOpen(false); load(); }}
      />

      <ClaudeExtractDialog
        open={claudeExtractOpen}
        contract={contract}
        onClose={() => setClaudeExtractOpen(false)}
        onApplied={() => { setClaudeExtractOpen(false); load(); }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => !deleting && setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Contract</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>This action is permanent and cannot be undone.</Alert>
          <Typography variant="body2" gutterBottom>
            Are you sure you want to delete <strong>{contract.contract_name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All attached documents and files will also be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            disabled={deleting}
            onClick={async () => {
              setDeleting(true);
              try {
                await contractsApi.delete(contract.id);
                navigate('/contracts');
              } catch (err: any) {
                setError(err.response?.data?.error || 'Failed to delete contract');
                setDeleteConfirmOpen(false);
              } finally {
                setDeleting(false);
              }
            }}
          >
            {deleting ? 'Deleting…' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/**
 * Renders text with all occurrences of `query` highlighted in yellow.
 */
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query || !text) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <mark key={i} style={{ background: '#fff176', padding: '0 1px', borderRadius: 2 }}>{part}</mark>
          : part
      )}
    </>
  );
}

function ContractHistory({ contractId }: { contractId: string }) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    contractsApi.history(contractId).then(res => { setHistory(res.data); setLoading(false); });
  }, [contractId]);

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const ACTION_META: Record<string, { label: string; color: string }> = {
    CREATE: { label: 'created',  color: '#2e7d32' },
    UPDATE: { label: 'updated',  color: '#1565c0' },
    DELETE: { label: 'deleted',  color: '#c62828' },
    VIEW:   { label: 'viewed',   color: '#757575' },
    DOWNLOAD: { label: 'downloaded a document for', color: '#6a1b9a' }
  };

  const formatValue = (v: any) => {
    if (v === null || v === undefined || v === '') return <em style={{ color: '#9e9e9e' }}>empty</em>;
    if (v === 1 || v === '1') return 'Yes';
    if (v === 0 || v === '0') return 'No';
    return String(v);
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box>
      {history.map((entry, i) => {
        const meta = ACTION_META[entry.action] || { label: entry.action.toLowerCase() + 'd', color: '#757575' };
        const hasChanges = entry.changes && entry.changes.length > 0;
        const isOpen = expanded.has(entry.id);

        return (
          <Box key={entry.id} display="flex" gap={2} mb={2.5}>
            {/* Timeline dot */}
            <Box display="flex" flexDirection="column" alignItems="center">
              <Avatar sx={{ width: 32, height: 32, bgcolor: meta.color, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                {entry.user_name?.[0]?.toUpperCase() || '?'}
              </Avatar>
              {i < history.length - 1 && (
                <Box sx={{ width: 2, flexGrow: 1, mt: 0.5, bgcolor: 'divider', minHeight: 16 }} />
              )}
            </Box>

            {/* Content */}
            <Box flex={1} pb={0.5}>
              <Box display="flex" alignItems="baseline" gap={0.5} flexWrap="wrap">
                <Typography variant="body2" fontWeight={600}>{entry.user_name || 'System'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  <span style={{ color: meta.color, fontWeight: 500 }}>{meta.label}</span> this contract
                </Typography>
                {hasChanges && (
                  <Chip
                    label={`${entry.changes.length} field${entry.changes.length !== 1 ? 's' : ''} changed`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: 10, cursor: 'pointer', ml: 0.5 }}
                    onClick={() => toggleExpand(entry.id)}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {format(parseISO(entry.created_at), 'MMM d, yyyy h:mm a')}
              </Typography>

              {/* Changed fields — expandable */}
              {hasChanges && isOpen && (
                <Box mt={1} sx={{ bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: 12, width: '30%' }}>Field</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: 12 }}>From</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: 12 }}>To</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entry.changes.map((change: any) => (
                        <TableRow key={change.field}>
                          <TableCell sx={{ py: 0.75, fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>
                            {change.label}
                          </TableCell>
                          <TableCell sx={{ py: 0.75, fontSize: 12, color: 'error.main', maxWidth: 200, wordBreak: 'break-word' }}>
                            {formatValue(change.old)}
                          </TableCell>
                          <TableCell sx={{ py: 0.75, fontSize: 12, color: 'success.main', maxWidth: 200, wordBreak: 'break-word' }}>
                            {formatValue(change.new)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
      {history.length === 0 && <Typography color="text.secondary">No history available</Typography>}
    </Box>
  );
}

